// RapidAlertSlideout.ts
import {ClickEvent, DataSourceRemoteDataChangeEvent, Layout, ModelDataChangeType, Image, SlideoutDecorator, TableRowDisplayEvent, DataSourceExecutionEvent, Tabset, TabStyle, Tab, Textbox, Table, TextboxVariant, TableRow, Checkbox, SelectCheckboxHeadingTableCell, ContextMenuLabelProps, ChangeEvent } from "@mcleod/components";
import { AutogenLayoutRapidAlertSlideout } from "./autogen/AutogenLayoutRapidAlertSlideout";
import { Api, HorizontalAlignment, UserSettings } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { TableRapidAlerts } from "./TableRapidAlerts";
import { ListenerListDef } from "@mcleod/components/src/base/ListenerListDef";

export class RapidAlertSlideout extends AutogenLayoutRapidAlertSlideout {
    private alertCount: number = 0; // Use a private field to store the count
    private filterTextbox: Textbox;
    private isExpanded: boolean = false;
    private tableSelectionState: Map<string, boolean> = new Map(); // Track selection state for each table

    override onLoad(): void {
        this.buttonConfirmedSelected.enabled = false; // Initially disable the button
        if (!UserSettings.get().enable_alerts) {
            const _remoteDataChangeListenerDef: ListenerListDef = { listName: "_remoteDataChangeListeners" };
            this.mainDataSource.removeAllEventListeners(_remoteDataChangeListenerDef);
        }
    }

    static showSlideout(): SlideoutDecorator {
        const layout = Layout.getLayout("lme/dispatch/RapidAlertSlideout") as RapidAlertSlideout;
        const rapidAlertSlideout = new SlideoutDecorator({
            layout: layout,
            width: "30%",
            height: "100%",
            title: "Alert Feed",
            layoutLoadListeners: async () => {
               layout.createFilterTextbox();
            }
        });
        return rapidAlertSlideout;
    }

    private createFilterTextbox() {
        this.filterTextbox = new Textbox({
            id: "globalFilterTextbox",
            fontSize: 16,
            captionVisible: false,
            variant: TextboxVariant.UNDERLINED,
            rowBreak: false,
            placeholder: "Filter",
        });

        this.filterTextbox.addChangeListener(() => {
        this.panelAccordions.components.forEach(component => {
            if (component instanceof Tabset) {
                const tableRapidAlerts = component.findComponentById("layoutTableRapidAlerts") as TableRapidAlerts;
                tableRapidAlerts.applyFilter(this.filterTextbox.valueAsString);
            }
        });
    })

        this.insertBefore(this.filterTextbox, this.horizontalspacer1);
    }

    buttonExpandAllOnClick(event: ClickEvent) {
        this.panelAccordions.components.forEach(component => {
            if (component instanceof Tabset) {
                component.components.forEach(tab => {
                    if (tab instanceof Tab) {
                        tab.expanded = !this.isExpanded;
                    }
                });
            }
        });
        this.isExpanded = !this.isExpanded;
        this.buttonExpandAll.caption = this.isExpanded ? "Collapse All" : "Expand All";
    }

    private updateConfirmButtonState(): void {
        // Enable button if any table has selected rows
        const hasSelectedRows = Array.from(this.tableSelectionState.values()).some(hasSelection => hasSelection);
        this.buttonConfirmedSelected.enabled = hasSelectedRows;
    }

    private handleTableSelectionChange = (tableId: string, hasSelectedRows: boolean): void => {
        this.tableSelectionState.set(tableId, hasSelectedRows);
        this.updateConfirmButtonState();
    }

    async buttonConfirmedSelectedOnClick(event: ClickEvent) {
        this.buttonConfirmedSelected.enabled = false;
        try {
            // Gather all selected row IDs across all accordions
            const ids: string[] = [];
            this.getAllTableRapidAlerts().forEach(tableRapidAlerts => {
                tableRapidAlerts.tableRapidAlerts.selectedRows.forEach(row => {
                    const id = row.data.get("id");
                    if (id) ids.push(id);
                });
            });
            if (ids.length > 0) {
                await Api.update("lme/dispatch/rapid-alert-msg", {
                    ids,
                    read: true,
                    confirm: true
                });
            }
            // Optionally clear selections after confirm
            this.getAllTableRapidAlerts().forEach(tableRapidAlerts => {
                tableRapidAlerts.tableRapidAlerts.selectedRows = [];
            });
        } catch (error) {
            console.error("Error confirming selected alerts:", error);
        } finally {
            this.updateConfirmButtonState();
        }
    }

    sourceRapidAlertOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        let changeType = event.change.type;
        const changeData = event.change.data;

        if (changeData.get("is_confirmed") || UserSettings.getUserId() !== changeData.get("user_id")) {
            changeType = ModelDataChangeType.DELETE;
        }

        this.sourceRapidAlert.handleDataChange({
            type: changeType,
            data: changeData,
            lastUpdateNanos: event.change.lastUpdateNanos
        });
        if (changeType == ModelDataChangeType.UPDATE) {
            const id = changeData.get("id");
            const existingRow: boolean = this.mainDataSource.data.some(row => row.get("id") === id);
            if (!existingRow) {
                this.mainDataSource.addRow(changeData);
            }
        }
        this.updateAccordions();
        event.preventDefault();
    }

    sourceRapidAlertAfterExecution(event: DataSourceExecutionEvent) {
        this.updateAccordions();
    }

    private updateAccordions() {
        // Get unique alert types
        const alertTypes = [...new Set(this.sourceRapidAlert.data.map(item => item.get("alert_id")))];

        // Remove accordions that no longer have data
        this.panelAccordions.components.forEach(component => {
            if (component instanceof Tabset) {
                const alertType = component.id.replace("accordionAlertType", "");
                const hasData = alertTypes.includes(alertType);
                if (!hasData) {
                    this.panelAccordions.remove(component);
                }
            }
        });

        // Add accordions for new alert types
        alertTypes.forEach(alertType => {
            const accordionExists = this.panelAccordions.components.some(component => component instanceof Tabset && component.id === `accordionAlertType${alertType}`);
            if (!accordionExists) {
                const accordion = new Tabset({
                    id: `accordionAlertType${alertType}`, fillRow: true, rowBreak: true, tabStyle: TabStyle.ACCORDION
                });
                const alertDescription = this.sourceRapidAlert.data.find(item => item.get("alert_id") === alertType)?.get("alert_description") || alertType;
                const tableRapidAlerts = Layout.getLayout("lme/dispatch/TableRapidAlerts") as TableRapidAlerts;
                tableRapidAlerts.alertType = alertType;
                tableRapidAlerts.alertDescription = alertDescription;
                tableRapidAlerts.id = `layoutTableRapidAlerts`;

                // Set up selection change callback
                const tableId = tableRapidAlerts.id + `__${alertType}`;
                this.tableSelectionState.set(tableId, false); // Initialize with no selection
                tableRapidAlerts.setOnSelectionChangeCallback((hasSelectedRows) => {
                    this.handleTableSelectionChange(tableId, hasSelectedRows);
                });

                // Set up getAllSelectedRows callback
                tableRapidAlerts.getAllSelectedRows = () => {
                    const allSelected: TableRow[] = [];
                    this.panelAccordions.components.forEach(component => {
                        if (component instanceof Tabset) {
                            const tra = component.findComponentById("layoutTableRapidAlerts") as TableRapidAlerts;
                            if (tra && tra.tableRapidAlerts && tra.tableRapidAlerts.selectedRows.length > 0) {
                                allSelected.push(...tra.tableRapidAlerts.selectedRows);
                            }
                        }
                    });
                    return allSelected.length > 0 ? allSelected : [tableRapidAlerts.tableRapidAlerts.selectedRow].filter(Boolean);
                };
                
                const tab = new Tab({ id: `tabAlertType${alertType}`, components: [tableRapidAlerts], caption: alertDescription });
                tableRapidAlerts.tab = tab;
                accordion.add(tab);
                this.panelAccordions.add(accordion);
                tableRapidAlerts.addLayoutLoadListener(() => {
                    const tableColumCheckBox = tableRapidAlerts.tableRapidAlerts.columns.find(col => col.headingCell instanceof SelectCheckboxHeadingTableCell);
                    if (tableColumCheckBox != null) {
                        const checkbox = (tableColumCheckBox.headingCell as SelectCheckboxHeadingTableCell).checkbox
                        checkbox.rowBreak = false;
                        tab.heading.insertBefore(checkbox, tab.headingLabel);
                        tableRapidAlerts.applyFilter(this.filterTextbox?.valueAsString ?? "");
                    }
                })
                this.setTabHeadingsContextMenu(accordion);
            }
        });

        // Remove tracking for tables that are no longer present
        const activeTables = alertTypes.map(alertType => `layoutTableRapidAlerts_${alertType}`);
        const staleTableIds = Array.from(this.tableSelectionState.keys())
            .filter(tableId => !activeTables.includes(tableId));
        
        staleTableIds.forEach(tableId => {
            this.tableSelectionState.delete(tableId);
        });
        
        this.updateConfirmButtonState();
    }

    // Utility to get all TableRapidAlerts for all accordions
    private getAllTableRapidAlerts(): TableRapidAlerts[] {
        const tables: TableRapidAlerts[] = [];
        this.panelAccordions.components.forEach(component => {
            if (component instanceof Tabset) {
                const tra = component.findComponentById("layoutTableRapidAlerts") as TableRapidAlerts;
                if (tra) tables.push(tra);
            }
        });
        return tables;
    }

    // Shared logic for marking all as read/confirmed
    private async markAllAlerts(action: "read" | "confirm") {
        const tables = this.getAllTableRapidAlerts();
        // Gather all selected row IDs across all accordions
        const ids: string[] = [];
        tables.forEach(tableRapidAlerts => {
            tableRapidAlerts.tableRapidAlerts.selectedRows.forEach(row => {
                const id = row.data.get("id");
                if (id) ids.push(id);
            });
        });
        if (ids.length === 0) return;
        // Prepare payload
        const payload: any = { ids };
        if (action === "read") {
            payload.read = true;
        } else if (action === "confirm") {
            payload.read = true;
            payload.confirm = true;
        }
        await Api.update("lme/dispatch/rapid-alert-msg", payload);
    }

    private setTabHeadingsContextMenu(tabset: Tabset) {
        tabset.components.forEach(tab => {
            if (tab instanceof Tab && tab.heading) {
                tab.heading.contextMenuItems = () => this.generateTabContextMenuItems(tab);
            }
        });
    }

    // Context menu for tab headings: acts on selected rows across all accordions
    private generateTabContextMenuItems(tab: Tab): Partial<ContextMenuLabelProps>[] {
        // Gather all selected rows from all accordions
        const allSelectedRows: TableRow[] = [];
        this.getAllTableRapidAlerts().forEach(tableRapidAlerts => {
            if (tableRapidAlerts.tableRapidAlerts?.selectedRows?.length > 0) {
                allSelectedRows.push(...tableRapidAlerts.tableRapidAlerts.selectedRows);
            }
        });

        if (allSelectedRows.length === 0) {
            return [{ text: "No rows selected" }];
        }
        return [
            {
                text: "Mark Read",
                onClick: async () => { await this.markAllAlerts("read"); }
            },
            {
                text: "Mark Confirmed",
                onClick: async () => { await this.markAllAlerts("confirm"); }
            }
        ];
    }

    tableRapidAlertsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const isRead = tableRow.data.get("is_read");
        const labelAlertID = tableRow.findComponentById("labelAlertID");
        if (!isRead) {
            labelAlertID.fontBold = true;
        } else {
            labelAlertID.fontBold = false;
        }
    }
}